<!--
Copyright 2017 ODK Central Developers
See the NOTICE file at the top-level directory of this distribution and at
https://github.com/getodk/central-frontend/blob/master/NOTICE.

This file is part of ODK Central. It is subject to the license terms in
the LICENSE file found in the top-level directory of this distribution and at
https://www.apache.org/licenses/LICENSE-2.0. No part of ODK Central,
including this file, may be copied, modified, propagated, or distributed
except according to the terms contained in the LICENSE file.
-->
<template>
  <div id="user-edit-password" class="panel panel-simple">
    <div class="panel-heading">
      <h1 class="panel-title">{{ $t('title') }}</h1>
    </div>
    <div class="panel-body">
      <form v-if="user != null && user.id === currentUser.id"
        @submit.prevent="submit">
        <input :value="currentUser.email" autocomplete="username">
        <form-group id="user-edit-password-old-password" v-model="oldPassword"
          type="password" :placeholder="$t('field.oldPassword')" required
          autocomplete="current-password"/>
        <form-group id="user-edit-password-new-password" v-model="newPassword"
          type="password" :placeholder="$t('field.newPassword')" required
          :has-error="mismatch" autocomplete="new-password"/>
        <form-group id="user-edit-password-confirm" v-model="confirm"
          type="password" :placeholder="$t('field.passwordConfirm')" required
          :has-error="mismatch" autocomplete="new-password"/>
        <button :disabled="awaitingResponse" type="submit"
          class="btn btn-primary">
          {{ $t('action.change') }} <spinner :state="awaitingResponse"/>
        </button>
      </form>
      <template v-else>
        {{ $t('cannotChange') }}
      </template>
    </div>
  </div>
</template>

<script>
import FormGroup from '../../form-group.vue';
import Spinner from '../../spinner.vue';
import request from '../../../mixins/request';
import { apiPaths } from '../../../util/request';
import { noop } from '../../../util/util';
import { requestData } from '../../../store/modules/request';

export default {
  name: 'UserEditPassword',
  components: { FormGroup, Spinner },
  mixins: [request()],
  data() {
    return {
      awaitingResponse: false,
      oldPassword: '',
      newPassword: '',
      confirm: '',
      mismatch: false
    };
  },
  computed: requestData(['currentUser', 'user']),
  watch: {
    $route() {
      this.oldPassword = '';
      this.newPassword = '';
      this.confirm = '';
      this.mismatch = false;
    }
  },
  methods: {
    submit() {
      this.mismatch = this.newPassword !== this.confirm;
      if (this.mismatch) {
        this.$alert().danger(this.$t('alert.mismatch'));
        return;
      }
      const data = { old: this.oldPassword, new: this.newPassword };
      this.put(apiPaths.password(this.user.id), data)
        .then(() => {
          this.$alert().success(this.$t('alert.success'));

          // The Chrome password manager does not realize that the form was
          // submitted. Should we navigate to a different page so that it does?
        })
        .catch(noop);
    }
  }
};
</script>

<style lang="scss">
#user-edit-password input[autocomplete="username"] {
  display: none;
}
</style>

<i18n lang="json5">
{
  "en": {
    // This is a title shown above a section of the page.
    "title": "Change Password",
    "action": {
      "change": "Change password"
    },
    "cannotChange": "Only the owner of the account may directly set their own password.",
    "alert": {
      "mismatch": "Please check that your new passwords match.",
      "success": "Success! Your password has been updated."
    }
  }
}
</i18n>

<!-- Autogenerated by destructure.js -->
<i18n>
{
  "cs": {
    "title": "Změnit heslo",
    "action": {
      "change": "Změnit heslo"
    },
    "cannotChange": "Pouze majitel účtu si může přímo nastavit vlastní heslo.",
    "alert": {
      "mismatch": "Zkontrolujte, zda se vaše nová hesla shodují.",
      "success": "Úspěch! Vaše heslo bylo aktualizováno."
    }
  },
  "de": {
    "title": "Passwort ändern",
    "action": {
      "change": "Passwort ändern"
    },
    "cannotChange": "Nur der Besitzer des Kontos kann sein eigenes Passwort direkt setzen.",
    "alert": {
      "mismatch": "Bitte prüfen Sie, dass ihre neuen Passwörter übereinstimmen.",
      "success": "Ihr Passwort wurde erfolgreich aktualisiert."
    }
  },
  "es": {
    "title": "Cambiar contraseña",
    "action": {
      "change": "Cambiar contraseña"
    },
    "cannotChange": "Solo el propietario de la cuenta puede establecer directamente su contraseña.",
    "alert": {
      "mismatch": "Verifique que sus nuevas credenciales coincidan.",
      "success": "¡Éxito! Su contraseña ha sido actualizada."
    }
  },
  "fr": {
    "title": "Changer le mot de passe",
    "action": {
      "change": "Changer le mot de passe"
    },
    "cannotChange": "Seul le propriétaire du compte peut directement définir son propre mot de passe.",
    "alert": {
      "mismatch": "Veuillez vérifier que vos nouveaux mots de passe correspondent.",
      "success": "Succès ! Votre mot de passe a été mis à jour."
    }
  }
}
</i18n>
